import { configForDownload, configForJsonPost } from '@/connection/base';
import { SharedBuergschaftService } from '@/shared/api/services/buergschaft';
import type { BuergeDaten } from '@/types/Buergschaftsauftrag.ts';
import type {
  Bürgschaft as Buergschaft,
  Bürgschaftskorrektur as Buergschaftskorrektur,
  ManuelleBuergschaft,
} from '@/types/Bürgschaft.ts';
import type { Inanspruchnahme } from '@/shared/Buergschaften/inanspruchnahme/types.ts';
import type {
  BuergschaftErstellenDto,
  BuergschaftErstellenResponse,
} from '@/shared/Buergschaften/types/BuergschaftErstellenDTO.ts';
import type { Dokument } from '@/types/DokumentUpload.ts';

export class BuergschaftService extends SharedBuergschaftService {
  async pruefeBuergschaftIdBuerge(buergschaftsIdBuerge: string): Promise<boolean> {
    return this.httpClient.get(`${this.buergschaftServiceBaseUrl}/buergschaft/fremdid/vorhanden`, {
      params: {
        buergschaftsIdBuerge,
      },
    });
  }

  async storniereBuergschaftsentwurf(
    buergschaftId: string,
    buergendaten: BuergeDaten,
  ): Promise<boolean> {
    return this.httpClient.post(
      `${this.buergschaftServiceBaseUrl}/buergschaften/${buergschaftId}/stornieren`,
      {
        data: buergendaten,
        config: configForJsonPost,
      },
    );
  }

  async pruefeManuelleBuergschaft(buergschaft: unknown, id: number): Promise<Buergschaft> {
    return this.httpClient.post(
      `${this.buergschaftServiceBaseUrl}/buergschaften/${id}/zweitpruefung`,
      {
        data: buergschaft,
        config: configForJsonPost,
      },
    );
  }

  async holeKorrekturdaten(buergschaftId: number): Promise<Buergschaftskorrektur> {
    return this.httpClient.get(
      `${this.buergschaftServiceBaseUrl}/buergschaften/${buergschaftId}/korrektur/grundlage`,
    );
  }

  async holeInanspruchnahme(inanspruchnahmeId: number): Promise<Inanspruchnahme> {
    return this.httpClient.get(
      `${this.buergschaftServiceBaseUrl}/buergschaften/inanspruchnahmen/${inanspruchnahmeId}`,
    );
  }

  async lehneManuelleBuergschaftAb(id: number): Promise<Buergschaft> {
    return this.httpClient.post(
      `${this.buergschaftServiceBaseUrl}/buergschaften/${id}/ablehnung/bestaetigung`,
    );
  }

  async downloadInanspruchnahmeDokumentationZurInanspruchnahme(
    buergschaftId: number,
  ): Promise<BlobPart> {
    return this.httpClient.get(
      `${this.buergschaftServiceBaseUrl}/buergschaften/${buergschaftId}/dokumentation/inanspruchnahmen`,
      { config: configForDownload },
    );
  }

  async bestaetigeInanspruchnahmeDokumentationDownload(
    buergschaftId: number,
    inanspruchnahmeId: number,
  ): Promise<void> {
    return this.httpClient.post(
      `${this.buergschaftServiceBaseUrl}/buergschaften/${buergschaftId}/dokumentation/inanspruchnahmen/${inanspruchnahmeId}`,
    );
  }

  async bestaetigeInanspruchnahmeDokumentationDownloadFuerAlleInanspruchnahmen(
    buergschaftId: number,
  ): Promise<void> {
    return this.httpClient.post(
      `${this.buergschaftServiceBaseUrl}/buergschaften/${buergschaftId}/dokumentation/inanspruchnahmen/benachrichtigung/alle`,
    );
  }

  async erstelleAltbuergschaft(buergschaft: unknown): Promise<Buergschaft> {
    return this.httpClient.post(`${this.buergschaftServiceBaseUrl}/altbuergschaft`, {
      data: buergschaft,
    });
  }

  async erstelleManuelleBuergschaft(
    buergschaft: ManuelleBuergschaft | BuergschaftErstellenDto,
    dokument?: Dokument,
  ): Promise<Buergschaft | BuergschaftErstellenResponse> {
    return this.httpClient.post(`${this.buergschaftServiceBaseUrl}/buergschaften`, {
      data: { ...buergschaft, dokument },
    });
  }

  async korrigiereManuelleBuergschaft(buergschaft: unknown, id: number): Promise<Buergschaft> {
    return this.httpClient.post(
      `${this.buergschaftServiceBaseUrl}/buergschaften/${id}/korrigierung/ersatz`,
      { data: buergschaft },
    );
  }
}
